import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { catchError, Observable, of, Subscription, tap } from 'rxjs';
import { SortBy } from '../../enums/sortby.enum';
import { UserCacheService } from 'src/app/xtra/user-cache/user-cache-service';

/**
 * Type for sorting options matching container-overview component.
 */
export type ImageSortingOptions = 'new-old' | 'old-new' | 'A-Z' | 'Z-A';

/**
 * Reusable search field component for filtering containers by name.
 *
 * Features:
 * - Search input with icon
 * - Enter key, blur, and Escape key handling
 * - Search results display with reset functionality
 * - Sorting dropdown
 * - Supports fixed or dynamic width
 *
 * @example
 * ```html
 * <app-container-search-field
 *   [placeholderTranslationKey]="'project.components.communityProject.searchPlaceholder'"
 *   [initialSearchText]="searchText"
 *   [width]="'dynamic'"
 *   [initialSortOption]="'new-old'"
 *   [resetTrigger$]="resetSearchTrigger$"
 *   type="gallery"
 *   (searchChanged)="onSearchChanged($event)"
 * ></app-container-search-field>
 * ```
 */
@Component({
    selector: 'app-container-search-field',
    templateUrl: './container-search-field.component.html',
    styleUrls: ['./container-search-field.component.scss'],
    standalone: false,
})
export class ContainerSearchFieldComponent
    implements OnInit, OnChanges, OnDestroy
{
    /**
     * Translation key for the input placeholder text.
     */
    @Input() placeholderTranslationKey: string | undefined;

    /**
     * Initial search text value to display when component loads.
     */
    @Input() initialSearchText: string = '';

    /**
     * Width mode: 'fixed' (400px) or 'dynamic' (100%).
     * Default: 'fixed'
     */
    @Input() width: 'fixed' | 'dynamic' = 'fixed';

    /**
     * Initial sort option value.
     * Default: 'new-old'
     */
    @Input() initialSortOption: ImageSortingOptions = 'new-old';

    /**
     * Observable input that triggers a reset of the search field when it emits.
     * When a new value is emitted, the search input is cleared and searchCleared event is emitted.
     */
    @Input() resetTrigger$: Observable<void> | undefined;

    @Input() type: string;

    /**
     * Emitted when search text changes (on Enter, blur, or icon click).
     * Emits the trimmed search text value.
     */
    @Output() searchChanged: EventEmitter<{
        searchText: string;
        sortOption: { sortBy: SortBy; asc: boolean };
    }> = new EventEmitter<{
        searchText: string;
        sortOption: { sortBy: SortBy; asc: boolean };
    }>();

    @ViewChild('searchInput', { static: false })
    searchInputRef: ElementRef<HTMLInputElement> | undefined;

    searchControl: FormControl = new FormControl('');
    sortControl: FormControl<ImageSortingOptions> =
        new FormControl<ImageSortingOptions>('new-old');
    hoveredFunctionalButtonIdx: number | null = null;
    private subscriptions: Subscription[] = [];

    /**
     * Available sorting options matching container-overview component.
     */
    readonly sortingOptions: ImageSortingOptions[] = [
        'new-old',
        'old-new',
        'A-Z',
        'Z-A',
    ];

    constructor(private userCacheService: UserCacheService) {}
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['type']) {
            this.searchControl.setValue(this.initialSearchText);
            this.sortControl.setValue(this.initialSortOption);

            this.userCacheService
                .loadData('imageContainerSortingForWorkspace' + this.type)
                .pipe(
                    tap((data: { sortingKey: ImageSortingOptions }) => {
                        this.sortControl.setValue(data.sortingKey);
                    }),
                    catchError(() => {
                        return of({ sortingKey: this.initialSortOption });
                    })
                )
                .subscribe(() => {
                    this.emitSearchChanged();
                });
        }
    }

    ngOnInit(): void {
        // Subscribe to reset trigger observable if provided
        if (this.resetTrigger$) {
            this.subscriptions.push(
                this.resetTrigger$.subscribe(() => {
                    this.searchControl.setValue('');
                    this.emitSearchChanged();
                })
            );
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) =>
            subscription.unsubscribe()
        );
    }

    /**
     * Handles sort option change - emits sortOptionChange event.
     */
    onSortOptionChange(): void {
        if (this.sortControl.value) {
            this.userCacheService
                .saveData('imageContainerSortingForWorkspace' + this.type, {
                    sortingKey: this.sortControl.value,
                })
                .pipe(
                    catchError(() => {
                        console.log(
                            'could not save sorting selection for ' + this.type
                        );
                        return of();
                    })
                )
                .subscribe(() => {});
            this.emitSearchChanged();
        }
    }

    /**
     * Emits searchChanged event.
     */
    private emitSearchChanged(): void {
        const trimmedValue = this.searchControl.value?.trim();
        this.searchChanged.emit({
            searchText: trimmedValue,
            sortOption: this.decodeSortingParams(
                this.sortControl?.value || this.initialSortOption
            ),
        });
    }

    /**
     * Handles Enter key press - emits search if value is not empty.
     */
    onEnter(): void {
        this.emitSearchChanged();
    }

    /**
     * Handles input blur event - emits search if value is not empty.
     */
    onBlur(): void {
        this.emitSearchChanged();
    }

    /**
     * Handles Escape key press - clears search and emits searchCleared event.
     * @param event - Keyboard event
     */
    onEscape(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.searchControl.setValue('');
            this.searchInputRef?.nativeElement.blur();
        }
    }

    /**
     * Handles search icon click - emits search if value is not empty.
     */
    onIconClick(): void {
        this.emitSearchChanged();
    }

    /**
     * Gets the current trimmed search term.
     * @returns The trimmed search text or empty string
     */
    get searchTerm(): string {
        return this.searchControl.value?.trim() ?? '';
    }

    private decodeSortingParams(sortOption: ImageSortingOptions): {
        sortBy: SortBy;
        asc: boolean;
    } {
        let sortBy: SortBy;
        let asc: boolean;
        switch (sortOption) {
            case 'new-old':
                sortBy = SortBy.CREATED;
                asc = false;
                break;
            case 'old-new':
                sortBy = SortBy.CREATED;
                asc = true;
                break;
            case 'A-Z':
                sortBy = SortBy.NAME;
                asc = true;
                break;
            case 'Z-A':
                sortBy = SortBy.NAME;
                asc = false;
                break;
        }
        return { sortBy, asc };
    }
}
